import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { api } from '../../services/api'; // For fetching options for select fields

const ItemDialog = ({ open, onClose, mode, itemData, categoryConfig, onSubmit, loading, error }) => {
  const [formData, setFormData] = useState({});
  const [selectOptions, setSelectOptions] = useState({}); // To store options for select fields loaded via API
  const [loadingOptions, setLoadingOptions] = useState({}); // To track loading state for each select field

  // Initialize form data when dialog opens or itemData/categoryConfig changes
  useEffect(() => {
    if (open && categoryConfig) {
      const initialData = {};
      categoryConfig.formFields.forEach(field => {
        if (mode === 'edit' && itemData && itemData.hasOwnProperty(field.name)) {
          initialData[field.name] = itemData[field.name];
        } else if (mode === 'edit' && itemData && field.type === 'boolean' || field.type === 'switch') {
          initialData[field.name] = itemData[field.name] !== undefined ? itemData[field.name] : (field.defaultValue !== undefined ? field.defaultValue : false);
        } else {
          initialData[field.name] = (field.type === 'boolean' || field.type === 'switch' 
            ? (field.defaultValue !== undefined ? field.defaultValue : false) 
            : (field.defaultValue !== undefined ? field.defaultValue : ''));
        }
      });
      setFormData(initialData);
    } else if (!open) {
      setFormData({});
      setSelectOptions({});
      setLoadingOptions({});
    }
  }, [open, mode, itemData, categoryConfig]);

  // Fetch options for select fields that source data from an API path
  const fetchSelectOptions = useCallback(async (fieldConfig) => {
    if (fieldConfig.type === 'select' && fieldConfig.optionsSource && (!selectOptions[fieldConfig.name] || selectOptions[fieldConfig.name].length === 0)) {
      setLoadingOptions(prev => ({ ...prev, [fieldConfig.name]: true }));
      try {
        const response = await api.get(fieldConfig.optionsSource);
        const options = response.data?.results || response.data || [];
        setSelectOptions(prev => ({ ...prev, [fieldConfig.name]: options }));
      } catch (err) {
        console.error(`Failed to fetch options for ${fieldConfig.label}:`, err);
        // Optionally, set an error state for this specific select field
        // Or use a passed-in showSnackbar if that was desired for such errors
      } finally {
        setLoadingOptions(prev => ({ ...prev, [fieldConfig.name]: false }));
      }
    }
  }, [selectOptions]); // Removed selectOptions from dep array as it was causing issues if options were empty initially

  useEffect(() => {
    if (open && categoryConfig) {
      categoryConfig.formFields.forEach(field => {
        fetchSelectOptions(field);
      });
    }
  }, [open, categoryConfig, fetchSelectOptions]);


  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' || type === 'switch' ? checked : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const dataToSubmit = { ...formData };
    // Ensure boolean/switch values are booleans, not strings, if necessary by backend
    categoryConfig.formFields.forEach(field => {
      if (field.type === 'boolean' || field.type === 'switch') {
        dataToSubmit[field.name] = !!dataToSubmit[field.name]; // Coerce to boolean
      }
      // Convert to number if type is number and value is string digit
      if (field.type === 'number' && typeof dataToSubmit[field.name] === 'string') {
        const numVal = parseFloat(dataToSubmit[field.name]);
        if (!isNaN(numVal)) {
            dataToSubmit[field.name] = numVal;
        }
      }
    });
    onSubmit(dataToSubmit);
  };

  if (!categoryConfig) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'primary.contrastText',
        fontSize: '1.25rem',
        fontWeight: 600
      }}>
        {mode === 'add' ? 'Add New' : 'Edit'} {categoryConfig.label.slice(0,-1)}
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ mb: 3, color: 'text.secondary' }}>
            Please fill in the details for the {categoryConfig.label.toLowerCase().slice(0,-1)}.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          <Grid container spacing={3}>
            {categoryConfig.formFields.map((field) => (
              <Grid item xs={field.gridXS || 12} sm={field.gridSM || (field.gridXS === 12 ? 12 : 6)} key={field.name}>
                {field.type === 'switch' || field.type === 'boolean' ? (
                  <FormControlLabel
                    control={<Switch checked={!!formData[field.name]} onChange={handleChange} name={field.name} />}
                    label={field.label}
                    sx={{mt:1}}
                  />
                ) : field.type === 'select' ? (
                  <FormControl fullWidth margin="dense" required={field.required} sx={{ position: 'relative' }}>
                    <InputLabel id={`${field.name}-select-label`}>{field.label}</InputLabel>
                    <Select
                      labelId={`${field.name}-select-label`}
                      name={field.name}
                      value={formData[field.name] || ''}
                      label={field.label}
                      onChange={handleChange}
                      sx={{ 
                        minHeight: '56px',
                        '& .MuiSelect-select': {
                          py: 2
                        }
                      }}
                    >
                      <MenuItem value=""><em>Select {field.label}</em></MenuItem>
                      {(field.optionsSource ? selectOptions[field.name] : field.options)?.map((option) => (
                        <MenuItem 
                            key={option[field.optionValueField || field.optionValue || 'value']}
                            value={option[field.optionValueField || field.optionValue || 'value']}
                        >
                          {option[field.optionLabelField || field.optionLabel || 'label']}
                        </MenuItem>
                      ))}
                    </Select>
                    {loadingOptions[field.name] && (
                      <CircularProgress 
                        size={20} 
                        sx={{
                          position: 'absolute', 
                          right: 40, 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          zIndex: 1
                        }}
                      />
                    )}
                  </FormControl>
                ) : (
                  <TextField
                    autoFocus={categoryConfig.formFields.findIndex(f=>f.name === field.name) === 0} // Autofocus on the first field
                    required={field.required}
                    margin="dense"
                    name={field.name}
                    label={field.label}
                    type={field.type === 'email' ? 'email' : field.type === 'number' ? 'number' : 'text'}
                    fullWidth
                    variant="outlined"
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    multiline={field.type === 'textarea'}
                    rows={field.type === 'textarea' ? 3 : 1}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: field.type === 'textarea' ? 'auto' : '56px'
                      }
                    }}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: '16px 24px', 
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          gap: 2
        }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : (mode === 'add' ? 'Create' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ItemDialog; 